/**
 * Главная задача: что делать сейчас и почему.
 *
 * Порядок здесь не новый — он уже зашит в `allocateIncome`. Новое в том, что
 * он **произносится**. Человек, открывший приложение, видит числа: доступно
 * столько-то, долг такой-то, цели такие-то. Чего он не видит — что из этого
 * сейчас важнее всего. Приложение обязано назвать одну задачу, иначе выбор
 * остаётся тем же, с каким человек пришёл.
 *
 * Порядок — от самого разрушительного к самому отложенному:
 *
 * 1. Доход не покрывает обязательное. Это режим выхода: ни цели, ни подушка
 *    не имеют смысла, пока месяц не сходится в принципе.
 * 2. Дорогой долг. Гасить его выгоднее, чем копить: ставка по нему выше
 *    любого разумного дохода от накоплений.
 * 3. Нет подушки. Один месяц расходов — разница между неприятностью и
 *    катастрофой; без него любая поломка снова отправляет в кредит.
 * 4. Цели. Всё обязательное закрыто, и деньги идут на то, чего человек хочет.
 *
 * Решение приложения — не приговор: человек может назначить главной другую
 * задачу, и тогда возвращается она, а причина остаётся посчитанной, а не
 * подогнанной под выбор.
 */
import { ZERO, add, clampPos, formatMoney, share, sub, type Money } from './money.js';
import { conservativeIncome } from './available.js';
import { monthlyObligations } from './instances.js';
import { EXPENSIVE_RATE_PCT, annualCost, repaymentOrder, savedPerYear, totalAnnualCost } from './loans.js';
import { orderGoals } from './goals.js';
import { BUFFER_MONTHS, bufferStage } from './plan.js';
import type { PlainDate } from './date.js';
import type { Goal, IncomeStream, Loan, Obligation } from './types.js';

export type FocusKind = 'income_gap' | 'expensive_debt' | 'unknown_need' | 'no_buffer' | 'goals' | 'nothing_yet';

export interface Focus {
  kind: FocusKind;
  /** Что сейчас главное — короткой строкой. */
  title: string;
  /** Почему именно это. Показывается дословно. */
  why: string;
  /** Число, вокруг которого задача: дефицит, остаток долга, нехватка подушки. */
  amount: Money;
  /** Что делать — одно действие, а не список. */
  action: string;
  /** Что станет главным, когда эта задача закроется. */
  next: string | null;
  /** Задача сейчас актуальна по расчёту. */
  applicable: boolean;
  /** Задачу назначил человек, а не расчёт. */
  chosen: boolean;
}

export interface FocusInput {
  /**
   * Сегодняшняя дата.
   *
   * Нужна, чтобы месячная нагрузка считалась по календарю наступлений:
   * без неё годовой платёж снова стал бы ежемесячным.
   */
  today: PlainDate;
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  /** Обычные траты на повседневные нужды помимо обязательств. */
  needBaseline: Money;
  /**
   * Названы ли обычные траты. Без них ни запас, ни цели не оценить: месяц
   * жизни — самая большая статья. Не передано — названы, если больше нуля.
   */
  needKnown?: boolean;
  goals: Goal[];
  loans: Loan[];
  /** Человек назначил главной другую задачу. */
  override?: FocusKind;
}

/** Сто тысяч рублей в копейках: единица сравнения выгоды досрочного взноса. */
const HUNDRED_K = 10_000_000 as Money;

/**
 * Назвать главную задачу.
 *
 * Считается по тем же данным, что и план: выдумывать здесь нечего, не хватало
 * только вывода. Если человек выбрал задачу сам, возвращается она — но с той
 * же причиной, что посчитана.
 */
export function diagnose(input: FocusInput): Focus {
  const all = focusOptions(input);
  const picked = input.override ? all.find((f) => f.kind === input.override) : undefined;
  if (picked) return { ...picked, chosen: true };
  return all.find((f) => f.applicable) ?? all[all.length - 1]!;
}

/**
 * Все задачи по порядку — для экрана, где человек переставляет главное.
 *
 * Неактуальные не прячутся: увидеть, что подушка уже не проблема, так же
 * полезно, как узнать, что проблема — долг.
 */
export function focusOptions(input: FocusInput): Focus[] {
  const income = add(...input.incomeStreams.filter((s) => s.isActive).map(conservativeIncome));
  const needKnown = input.needKnown ?? input.needBaseline > ZERO;
  const obligationsMonthly = monthlyObligations(input.obligations, input.today);
  const monthlyNeed = add(obligationsMonthly, input.needBaseline);
  const gap = clampPos(sub(monthlyNeed, income));

  const open = input.loans.filter((l) => l.principal > ZERO);
  const worst = open
    .filter((l) => l.ratePct >= EXPENSIVE_RATE_PCT && annualCost(l) > ZERO)
    .sort((a, b) => b.ratePct - a.ratePct)[0];

  const buffer = input.goals.find((g) => g.kind === 'emergency_fund' && g.status !== 'cancelled');
  const bufferNow = buffer?.currentAmount ?? ZERO;
  const oneMonth = share(monthlyNeed, BUFFER_MONTHS.critical);
  const bufferGap = clampPos(sub(oneMonth, bufferNow));

  // Порядок целей — общий для всех движков: «главное» обязано указывать на ту
  // же цель, которой приложение попросит денег.
  const wanted = orderGoals(
    input.goals.filter((g) => g.status === 'active' && g.kind !== 'emergency_fund'),
  );
  const first = wanted[0];

  return [
    {
      kind: 'income_gap',
      applicable: gap > ZERO,
      chosen: false,
      title: 'Свести месяц',
      amount: gap,
      why: `Обязательное — платежи и жизнь — стоит ${formatMoney(monthlyNeed)} в месяц, `
        + `а приходит ${formatMoney(income)}. Не хватает ${formatMoney(gap)}.`,
      action: 'Пока месяц не сходится, цели и накопления подождут: сначала нужно закрыть разрыв — '
        + 'сократить расходы, передоговориться по платежам или добавить доход.',
      next: 'Когда месяц сойдётся, главным станет дорогой долг или подушка',
    },
    {
      kind: 'expensive_debt',
      applicable: worst !== undefined,
      chosen: false,
      title: worst ? `Закрыть долг: ${worst.name}` : 'Закрыть дорогой долг',
      amount: worst?.principal ?? ZERO,
      why: worst
        ? `Ставка ${fmtRate(worst.ratePct)} — этот долг обходится в `
          + `${formatMoney(annualCost(worst))} в год. Накопления столько не приносят, `
          + 'поэтому гасить выгоднее, чем копить.'
        : `Долг дороже ${EXPENSIVE_RATE_PCT}% годовых съедает больше, чем приносят накопления.`,
      action: expensiveAction(open, worst),
      next: 'Дальше — запас на один месяц расходов',
    },
    /*
      Обычные траты не названы — главное узнать их. Прежде здесь молча
      проходили и подушка, и цели, и у человека с пустыми данными главная
      писала «запас на месяц есть» при нуле на счетах (15.10.2026).
    */
    {
      kind: 'unknown_need',
      applicable: !needKnown,
      chosen: false,
      title: 'Назвать обычные траты',
      amount: ZERO,
      why: 'Пока неизвестно, сколько уходит на повседневные нужды, нельзя сказать ни сколько '
        + 'можно тратить, ни хватает ли запаса: месяц жизни — самая большая статья.',
      action: 'Назови примерную сумму в месяц — уточнить можно потом, по записанным тратам.',
      next: 'Потом — запас на месяц расходов и цели',
    },
    {
      kind: 'no_buffer',
      applicable: needKnown && monthlyNeed > ZERO && bufferStage(bufferNow, monthlyNeed) === 'critical',
      chosen: false,
      title: 'Собрать запас на месяц',
      amount: bufferGap,
      why: bufferNow > ZERO
        ? `В запасе ${formatMoney(bufferNow)}, а месяц жизни стоит ${formatMoney(monthlyNeed)}. `
          + 'Пока запаса меньше месяца, любая поломка снова уходит в кредит.'
        : `Запаса нет вовсе, а месяц жизни стоит ${formatMoney(monthlyNeed)}. `
          + 'Один месяц — это разница между неприятностью и катастрофой.',
      action: buffer
        ? `Довести подушку до ${formatMoney(oneMonth)}: не хватает ${formatMoney(bufferGap)}.`
        : `Завести цель «Подушка» на ${formatMoney(oneMonth)} — это один месяц твоих расходов.`,
      next: 'Потом — три месяца расходов и цели',
    },
    {
      kind: 'goals',
      applicable: wanted.length > 0,
      chosen: false,
      title: first
        ? (first.kind === 'debt_payoff' ? first.name : `Копить: ${first.name}`)
        : 'Копить на цели',
      amount: first ? clampPos(sub(first.targetAmount, first.currentAmount)) : ZERO,
      why: 'Обязательное закрыто, дорогих долгов нет, запас на месяц есть. '
        + 'Свободные деньги теперь работают на то, чего ты хочешь.',
      action: wanted.length > 1
        ? `Целей ${wanted.length}, и они конкурируют за одни деньги: приложение считает, `
          + 'что успеет к сроку, а что придётся сдвинуть.'
        : 'Взнос откладывается с прихода, а не из остатка в конце месяца.',
      next: null,
    },
    {
      kind: 'nothing_yet',
      applicable: true,
      chosen: false,
      title: 'Поставить цель',
      amount: ZERO,
      why: 'Обязательное закрыто, дорогих долгов нет, запас на месяц есть, '
        + 'а целей пока не поставлено. Без цели свободные деньги разойдутся сами.',
      action: 'Подушка до трёх месяцев, крупная покупка, отпуск — что угодно с суммой и сроком.',
      next: null,
    },
  ];
}

/**
 * Что делать с дорогим долгом — с оглядкой на остальные.
 *
 * Когда долг один, человеку нужна выгода в деньгах. Когда их несколько — он
 * уже знает, что должен, и не знает, с какого начинать: важнее очередь.
 */
function expensiveAction(open: Loan[], worst: Loan | undefined): string {
  if (!worst) return 'Гасить по очереди, начиная с самого дорогого.';
  const order = repaymentOrder(open);
  if (order.length <= 1) {
    return 'Всё свободное сверх платежа — в этот долг: каждые 100 000 ₽ экономят '
      + `${formatMoney(savedPerYear(worst, HUNDRED_K))} в год.`;
  }
  return `Долгов ${order.length}, вместе они стоят ${formatMoney(totalAnnualCost(open))} в год. `
    + `Первым гасить «${order[0]!.loan.name}»: там дороже всего каждый рубль.`;
}

const fmtRate = (r: number): string =>
  `${(Math.round(r * 10) / 10).toString().replace('.', ',')}%`;
